import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import OfferwallCard from "@/components/offerwall-card";
import TransactionItem from "@/components/transaction-item";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Trophy, Youtube, Package, Star, AlertTriangle, Loader, Gamepad2, Gift, LayoutGrid } from 'lucide-react';

interface OfferCompletion {
  id: string;
  provider: string;
  tokensEarned: number;
  status: 'completed' | 'pending' | 'failed';
  completedAt: string; // Dates will be stringified
  verificationData: {
    offerName?: string;
    gameName?: string;
    videoTitle?: string;
    [key: string]: any;
  };
}

interface PlatformStats {
  averageReward: number;
}

const fetchRecentActivity = async (): Promise<OfferCompletion[]> => {
  const response = await fetch("/api/transactions"); // This endpoint should be authenticated and return user-specific data
  if (!response.ok) {
    throw new Error("Failed to fetch recent activity");
  }
  return response.json();
};

const fetchPlatformStats = async (): Promise<PlatformStats> => {
  const response = await fetch("/api/platform-stats");
  if (!response.ok) {
    throw new Error("Failed to fetch platform stats");
  }
  return response.json();
};


const Earn = () => {
  const [visibleActivity, setVisibleActivity] = useState(5);
  const offerwalls = [
    {
      provider: "Sponsored Games",
      icon: <Gamepad2 className="w-6 h-6 text-white"/>,
      description: "Play & Earn",
      totalOffers: 24,
      color: "from-blue-500 to-purple-600",
      link: "/earn/sponsored-games",
      offers: [
          { name: "Play 'Tower Defense'", payout: 20 },
          { name: "Reach Level 10 in 'Obby'", payout: 10 },
      ],
    },
    {
      provider: "Lootably",
      icon: <Trophy className="w-6 h-6 text-white"/>,
      description: "High-Paying Offers",
      totalOffers: 18,
      color: "from-green-500 to-teal-600",
      link: "/earn/lootably",
      offers: [
          { name: "Complete a Survey", payout: 1500 },
          { name: "Install 'Puzzle Game'", payout: 500 },
      ],
    },
    {
      provider: "Clipping",
      icon: <Youtube className="w-6 h-6 text-white"/>,
      description: "Clip for Cash",
      totalOffers: 2,
      color: "from-red-500 to-orange-600",
      link: "/earn/clipping",
      offers: [
          { name: "Submit a Youtube Video", payout: 150 },
          { name: "Submit a TikTok Clip", payout: 50 },
      ],
    },
    {
        provider: "Promo Codes",
        icon: <Gift className="w-6 h-6 text-white"/>,
        description: "Bonus Rewards",
        totalOffers: 2, 
        color: "from-amber-500 to-yellow-600",
        link: "/promo-codes",
        offers: [
            { name: "Submit a promocode", payout: 5 },
            { name: "Submit a promocode", payout: 10 },
        ],
    },
  ];

  const { data: recentActivity, isLoading: isLoadingActivity, isError: isErrorActivity } = useQuery<OfferCompletion[]>({
    queryKey: ["recentUserActivity"], // Changed queryKey to be more specific
    queryFn: fetchRecentActivity 
  });

  const { data: platformStats, isLoading: isLoadingStats } = useQuery<PlatformStats>({ 
    queryKey: ["platformStats"], 
    queryFn: fetchPlatformStats 
  });

  const totalOffers = offerwalls.reduce((sum, wall) => sum + wall.totalOffers, 0);

  const getActivityDescription = (activity: OfferCompletion): string => {
    switch (activity.provider) {
        case 'lootably':
            return activity.verificationData?.offerName || 'Completed a Lootably offer';
        case 'sponsored_ad':
            return `Played ${activity.verificationData?.gameName || 'a sponsored game'}`;
        case 'clipping':
            return `Views on "${activity.verificationData?.videoTitle || 'your clip'}"`;
        default:
            return `Offer from ${activity.provider}`;
    }
  };

  return (
    <div className="py-20 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center">
            <LayoutGrid className="w-8 h-8 mr-3 text-primary"/>
            Earning Dashboard
          </h1>
          <p className="text-xl text-muted-foreground">Choose an earning method below to start collecting tokens</p>
        </motion.div>

        {/* Quick Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-12"
        >
             <Card className="glass-effect border-border">
                <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div className="flex flex-col items-center justify-center space-y-1">
                            <Package className="w-7 h-7 text-primary mb-1" />
                            <div className="text-2xl font-bold text-primary">
                                {totalOffers}
                            </div>
                            <div className="text-xs text-muted-foreground">Total Offers</div>
                        </div>
                        <div className="flex flex-col items-center justify-center space-y-1">
                            <Star className="w-7 h-7 text-accent mb-1" />
                            <div className="text-2xl font-bold text-accent">
                                {isLoadingStats ? <Loader className="w-6 h-6 animate-spin mx-auto" /> : Math.round(platformStats?.averageReward ?? 0)}
                            </div>
                            <div className="text-xs text-muted-foreground">Average Reward</div>
                        </div>
                        <div className="flex flex-col items-center justify-center space-y-1">
                            <Trophy className="w-7 h-7 text-secondary mb-1" />
                            <div className="text-2xl font-bold text-secondary">Lootably</div>
                            <div className="text-xs text-muted-foreground">Highest Paying</div>
                        </div>
                        <div className="flex flex-col items-center justify-center space-y-1">
                            <Gamepad2 className="w-7 h-7 text-blue-400 mb-1" />
                            <div className="text-2xl font-bold text-blue-400">Gaming</div>
                            <div className="text-xs text-muted-foreground">Most Fun</div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
        
        <h2 className="text-2xl font-bold mb-6 text-center md:text-left">Choose a Category</h2>
        {/* Offerwall Providers */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          {offerwalls.map((offerwall, index) => (
            <motion.div
              key={offerwall.provider}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + (0.05 * index), duration: 0.5 }}
            >
              <OfferwallCard {...offerwall} />
            </motion.div>
          ))}
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Card className="glass-effect border-border">
            <CardHeader>
              <CardTitle className="text-xl flex items-center"><Activity className="w-5 h-5 mr-2"/>Your Recent Earnings</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="space-y-4">
                {isLoadingActivity && (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    <span>Loading your earnings...</span>
                  </div>
                )}
                {isErrorActivity && (
                  <div className="flex items-center justify-center py-8 text-destructive">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    <span>Failed to load your activity.</span>
                  </div>
                )}
                {recentActivity && recentActivity.length > 0 && recentActivity.slice(0, visibleActivity).map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * index, duration: 0.4 }}
                  >
                    <TransactionItem 
                      id={activity.id}
                      type="earn"
                      description={getActivityDescription(activity)}
                      amount={activity.tokensEarned}
                      status={activity.status}
                      createdAt={new Date(activity.completedAt)}
                    />
                  </motion.div>
                ))}
                 {recentActivity && recentActivity.length === 0 && !isLoadingActivity && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>You haven't earned anything yet. Complete an offer to get started!</p>
                  </div>
                )}
              </div>
              {recentActivity && recentActivity.length > visibleActivity && (
                <div className="text-center mt-6">
                  <Button variant="ghost" onClick={() => setVisibleActivity(prev => prev + 5)}>
                    Load More
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Earn;
